import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { useUnsavedChanges } from "../../../components/layout/UnsavedChanges";
import {
  Button,
  EmptyState,
  SetupBanner,
  SetupBannerSkeleton,
  Tabs,
  failedEmptyState,
} from "../../../components/ui";
import { useCurrentUser } from "../../auth/hooks/useCurrentUser";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import { BillingSection } from "../../billing";
import AccountsTab from "../components/AccountsTab";
import PolicyTab from "../components/PolicyTab";
import ProfileForm from "../components/ProfileForm";
import SupportTab from "../components/SupportTab";
import ProfileOverview from "../components/ProfileOverview";
import ProfileOverviewSkeleton from "../components/ProfileOverviewSkeleton";
import { emptyProfile } from "../data/profileMock";
import { useDietitianProfile } from "../hooks/useDietitianProfile";
import { useProfileSave } from "../hooks/useProfileSave";
import { withAccountName } from "../profilePrefill";
import {
  SETTINGS_TABS,
  settingsTabFromParam,
  settingsTabLabel,
} from "../settingsTabs";
import { isProfileSetUp } from "../setupProgress";
import { validateProfile } from "../validation";
import type { DietitianProfile, SettingsTabId } from "../types/settings.types";

/**
 * Settings: the dietitian's own profile, what they pay us, and the account
 * itself — three tabs over one screen.
 *
 * General is the one with a screen behind it, and it has two faces: the
 * profile as it stands, and the form that edits it. The card comes first
 * because the commonest visit is someone checking a number rather than
 * changing one.
 */
const SettingsPage: React.FC = () => {
  // In the URL rather than in state, so the tab survives a refresh and can be
  // linked to — the same way the client detail screen carries its own.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = settingsTabFromParam(searchParams.get("tab"));

  // `replace`, so moving between tabs does not fill the back button with every
  // tab that was looked at along the way.
  const setActiveTab = (tab: SettingsTabId) =>
    setSearchParams({ tab }, { replace: true });

  const { loaded, status, reload } = useDietitianProfile();
  const { saving, save } = useProfileSave();

  // The name the account was opened under, which is what the form's first two
  // fields open with while the profile behind them has none — see
  // `profilePrefill`. In hand by the time the profile lands: this screen renders
  // behind `RequireAuth`, so Clerk has a session before the read is even sent.
  const { givenName, familyName } = useSignedInUser();
  const opening = (profile: DietitianProfile) =>
    withAccountName(profile, { givenName, familyName });

  // The one copy of "who is signed in" the rest of the app draws from. This
  // screen is where it changes, so this screen is what has to tell it — see
  // `handleSave`.
  const { showSaved, refresh: refreshAccount } = useCurrentUser();

  const [editing, setEditing] = useState(false);

  // `draft` is what the form edits. `saved` is the last committed copy — what
  // Cancel restores, and what Save compares against to decide whether there is
  // anything to send. It is also what the save itself is diffed against: the
  // `PATCH` carries only the fields that differ, so `saved` has to be the
  // record as the backend last confirmed it.
  const [draft, setDraft] = useState<DietitianProfile>(emptyProfile);
  const [saved, setSaved] = useState<DietitianProfile>(emptyProfile);

  // What arrived is both what the form shows and what it counts as committed,
  // so the screen opens filled in and with nothing to save.
  //
  // Seeded during the render that first sees the load rather than in an effect
  // after it: an effect would paint the empty form once and fill it a frame
  // later, which reads as the screen overwriting itself. React re-runs this
  // render with the new state before committing either version of it.
  //
  // Once per load, and `loaded` is a fresh object only when a fetch answered —
  // so a retry seeds again while an ordinary re-render does not.
  const [seededFrom, setSeededFrom] = useState<DietitianProfile | null>(null);
  if (loaded && loaded !== seededFrom) {
    setSeededFrom(loaded);
    // The draft opens with the account's name where the record carries none,
    // and `saved` stays exactly as it arrived — so the suggestion reads as a
    // change waiting to be saved rather than as something already stored.
    setDraft(opening(loaded));
    setSaved(loaded);

    // A profile nobody has filled in opens on the form rather than on the card
    // that reads it back. The overview of an empty profile is a screen of
    // blanks and an Edit button — every one of its lines is a value that is not
    // there — so the first visit is sent straight to the fields, and the strip
    // above them says why.
    //
    // Only on the seeding render, so this is the state the profile arrives in
    // rather than a rule that keeps reopening the form: someone who cancels out
    // of it gets the card, empty as it is, and is not put back.
    setEditing(!isProfileSetUp(loaded));
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const errors = validateProfile(draft);
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  /**
   * What the rail asks about, which is not quite what Save is armed by.
   *
   * `dirty` above is measured against the bare record, so a profile with no
   * name stored is dirty the moment it loads — the draft opened with the
   * account's name in it. That is right for the Save button, which is offering
   * to store the suggestion. It is wrong for a prompt: nobody has typed
   * anything, and stopping them on the way out to warn about work they did not
   * do is the sort of dialog people learn to click through.
   *
   * So this one is measured against what the form actually opened with. What
   * the guard protects is what the user entered, and the suggestion is not
   * that — it is read off the account again the next time the form opens.
   *
   * Only while the form is open: the overview beside it reads the profile back
   * and holds nothing of anyone's.
   */
  useUnsavedChanges(
    editing && JSON.stringify(draft) !== JSON.stringify(opening(saved)),
    {
      title: "Unsaved profile changes",
      body: "You’ve changed your profile details but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save changes",
    },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    {
      canSave: valid && !saving,
      save: () => commit(),
      report: () => problems.current?.(),
    }
  );

  const handleChange = (patch: Partial<DietitianProfile>) =>
    setDraft((current) => ({ ...current, ...patch }));

  /**
   * What came back is written into the draft as well as the saved copy: the
   * backend normalises as it stores — the numbers to `+91…`, the gender and the
   * specializations to codes and back — and the form has to show what was
   * actually kept rather than what was typed.
   *
   * A failed save commits neither, so the form stays open on the draft, stays
   * dirty, and is what the next Save retries. The message naming what went
   * wrong is the hook's — see `useProfileSave`.
   *
   * The name and the picture are also what the rail draws and what the
   * dashboard greets by, so the account context is told twice over: `showSaved`
   * puts what came back on screen in this same commit — the photo's bytes are
   * already in the browser, so there is nothing to wait for — and `refresh`
   * re-reads `/api/v1/me/` behind it, so what settles is the record as the
   * backend composed it rather than as this screen assumed.
   *
   * Answers with whether it landed, which is what the rail's prompt needs to
   * know before it lets a navigation through.
   */
  const commit = async (): Promise<boolean> => {
    const stored = await save(draft, saved);
    if (!stored) return false;

    setDraft(stored);
    setSaved(stored);
    setEditing(false);

    showSaved({
      firstName: stored.firstName,
      lastName: stored.lastName,
      // Whichever the form is holding: the local read of a picture just picked,
      // or the stored file's own URL for one that arrived on a load. Both paint
      // without a request. Empty where the picture was taken off, and the rail
      // goes back to drawing the initial.
      photoUrl: stored.photo?.dataUrl ?? "",
    });
    refreshAccount();

    return true;
  };

  const handleSave = () => {
    void commit();
  };

  // Back to what the form opened with rather than to the bare record: on a
  // profile with no name stored, that is the account's name again. Cancel undoes
  // the editing, and the suggestion was not part of it — reopening the form to
  // find the first field blank this time would read as the screen forgetting who
  // is signed in.
  const handleCancel = () => {
    setDraft(opening(saved));
    setEditing(false);
  };

  // The open profile form is the one thing on this screen that keeps its own
  // actions pinned, and it can only do that if the page hands it a height to
  // fill rather than growing with it. So from `lg` up the page owns the
  // viewport while the form is open — and only then: every other tab is as
  // tall as its cards and scrolls with the page, which is what `overflow-hidden`
  // would take away from them.
  const formOpen = activeTab === "general" && status === "ready" && editing;

  return (
    // Full height rather than as tall as its cards, so a tab that puts
    // something at the foot of the screen can — the billing section's refund
    // bar is the one that does. `AppLayout`'s scroll region is what that
    // height is measured against.
    <div
      className={`flex min-h-full flex-col p-page pt-page-top ${
        formOpen
          ? // The open form's own bar is the foot of the phone — the shell's
            // tab bar has stood down under it — so the page keeps no padding
            // beneath it there: scrolled to the end, the bar would otherwise
            // rest that far above the edge of the screen with nothing in the
            // gap. Only below `md`; the wider layouts still sit in the page's
            // own margin.
            "pb-0 md:pb-5 3xl:pb-8 lg:h-full lg:overflow-hidden"
          : ""
      }`}
    >
      {/* The tabs are what one screen of five tabs needs and what five
          separate entry points do not. On a phone this screen is no longer
          reached as "Settings" — the account menu lists Billings, Support,
          Accounts and Policy as destinations of their own, and the name at
          its head opens General — so a bar of the four they did not press is
          answering a question nobody asked. Hidden rather than not rendered:
          the tab is still what `?tab=` says, and from `md` up the screen is
          exactly what it was.

          The title stays, and on a phone it is the tab's own name rather than
          the screen's: what was pressed in the menu is what the page is
          called, the way every other screen a phone opens from that menu
          names itself at the top. General is the profile, and is called that
          — it is reached from the name at the head of the menu, not from a
          row saying "General". */}
      <PageHeader
        title={
          <>
            <span className="md:hidden">
              {activeTab === "general"
                ? "My profile"
                : settingsTabLabel(activeTab)}
            </span>
            <span className="hidden md:inline">Settings</span>
          </>
        }
      />

      <div className="mt-5 hidden md:block flex-shrink-0 border-b border-line">
        {/* The same list the trails under this screen are built from, so a
            crumb cannot call a tab by a name the tab bar does not use — see
            `settingsTabs`. */}
        <Tabs
          tabs={SETTINGS_TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {/* Over the panel rather than inside it, the way My brand carries its
          own: the strip belongs to the screen under the tabs, and the panel
          below is the height the open form is measured against.

          Worked out from the saved profile's own fields — nothing is asked of
          the backend for it — and from `saved` rather than `draft`, so it is
          the profile as stored that answers rather than the boxes as typed. A
          form filled in but not yet saved still carries the strip, and the
          response to Save is what takes it away: `handleSave` writes what came
          back into `saved`.

          The General tab only: what it asks for is on the profile under it, so
          on Billings or Support it would be a demand with nothing on screen to
          meet it. Its shape stands in while the read is out, so the strip and
          the cards land together. A load that failed knows nothing to report at
          all, and shows neither. */}
      {activeTab === "general" &&
        (status === "loading" ? (
          <SetupBannerSkeleton />
        ) : status === "ready" && !isProfileSetUp(saved) ? (
          <SetupBanner title="Complete your profile setup" />
        ) : null)}

      {/* The panel takes what is left of the page, and hands it to whichever
          tab is open — see the note above. `min-h-0` is what lets it be
          shorter than its contents, which is what makes the form scroll inside
          itself rather than stretching this box past the viewport.

          The 20px over it is the gap under the tab bar — and, on a phone,
          under the title that stands where the bar would. */}
      <div
        className={`flex flex-1 flex-col mt-5 ${formOpen ? "lg:min-h-0" : ""}`}
      >
        {activeTab === "general" &&
          // The profile only opens once there is something to open it with:
          // painting empty fields and filling them a moment later reads as the
          // screen overwriting what was typed into it.
          (status === "loading" ? (
            <ProfileOverviewSkeleton />
          ) : status === "failed" ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center card-surface border border-line bg-white">
              {/* Its own description rather than the generic one, and truer:
                  the tab only ever reads back what is already saved, so a
                  failure here cannot have cost anyone what they typed. */}
              <EmptyState
                {...failedEmptyState(
                  "We could not load your profile",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>
                )}
                description="Nothing has been lost - this is only what is saved being read back."
              />
            </div>
          ) : editing ? (
            <ProfileForm
              profile={draft}
              onChange={handleChange}
              errors={errors}
              onSave={handleSave}
              onCancel={handleCancel}
              dirty={dirty}
              saving={saving}
              // The same answer the strip above is drawn from, and from `saved`
              // for the same reason: it is the profile as stored that decides
              // whether this save finishes the setup or changes it, so the
              // button keeps its first-time wording until the record behind the
              // form is actually complete.
              setUp={isProfileSetUp(saved)}
              problems={problems}
            />
          ) : (
            <ProfileOverview profile={saved} onEdit={() => setEditing(true)} />
          ))}

        {/* What the practice pays Naurist: the plan in force, the invoices
            behind it, and the price list for a workspace that has none. It
            used to be `/billing`, opened from its own rail item — see
            `BillingSection`. The checkout it leads to is still a screen of its
            own, because paying is not a thing to do inside a tab. */}
        {activeTab === "billings" && <BillingSection />}

        {activeTab === "accounts" && <AccountsTab />}

        {activeTab === "support" && <SupportTab />}

        {activeTab === "policy" && <PolicyTab />}
      </div>
    </div>
  );
};

export default SettingsPage;
